import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { driverInfoFieldConfiguration } from "../../../formConfigs/Fleet/driverInfoFieldConfig";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";

const formatDateInput = (dateValue?: string | null) =>
  dateValue ? dateValue.split("T")[0] : null;
const formatDateTimeInput = (dateValue?: string | null) =>
  dateValue ? dateValue.replace("Z", "").slice(0, 16) : null;

const UpdateDriverPopup = ({ driver }: { driver: any }) => {
  const [settlementInfoData, setSettlementInfoData] = useState<OptionType[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<any>(null);

  const queryClient = useQueryClient();

  const handleOpen = (e: any) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setInitialValues(null);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const { token } = useAuth();

  const { data: settlementPayees, isSuccess } = useQuery({
    queryKey: ["settlementPayees"],
    queryFn: async () => {
      const res = await axios.get(`${apiVariable}/getSettlementPayees`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data.map((settlementPayee: any) => ({
        label: `${settlementPayee.payeeCode} | ${settlementPayee.name}`,
        id: settlementPayee.payeeCode,
      }));
    },
    enabled: open,
  });

  useEffect(() => {
    if (isSuccess && open) {
      const formattedData = settlementPayees;
      setSettlementInfoData(formattedData);
      setInitialValues({
        id: driver.id,
        firstName: driver.firstName,
        lastName: driver.lastName,
        phoneNumber: driver.phoneNumber,
        birthDate: formatDateInput(driver.birthDate),
        workStartDate: formatDateInput(driver.workStartDate),
        terminationDate: formatDateTimeInput(driver.terminationDate),
        company: driver.company,
        driverCode: driver.driverCode,
        driverType: capitalizeFirstChar(driver.driverType),
        usedEld: capitalizeFirstChar(driver.usedEld),
        payTo:
          formattedData.find((payee: any) => payee.id === driver.payTo) || "",
        payType: capitalizeFirstChar(driver.payType),
      });
    }
  }, [isSuccess, open, settlementPayees, driver]);

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/updateDriver`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");

      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit truck");
    },
  });

  const submitHandler = (data: any) => {
    const updatedData = {
      ...data,
      payTo: data.payTo
        ? typeof data.payTo === "object"
          ? data.payTo.id
          : data.payTo
        : driver.payTo,
    };
    mutation.mutate(updatedData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>
      {open && initialValues && (
        <GenericFormPopup
          formFields={driverInfoFieldConfiguration}
          initialValues={initialValues}
          title="Update Driver"
          onSubmit={submitHandler}
          open={open}
          onClose={handleClose}
          loading={mutation.isPending}
          autocompleteData={{ settlementPayees: settlementInfoData }}
        />
      )}

      <StatusSnackbar
        status={status}
        successMessage={"Driver updated successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateDriverPopup;
